import { useEffect, useState } from "react";
import { useVault } from "../hooks/useVault.jsx";
import { useToast } from "../hooks/useToast.jsx";
import { Modal, ConfirmDialog } from "../components/Modal.jsx";
import * as api from "../lib/api.js";
import * as AI from "../lib/ai.js";
import * as Notify from "../lib/notify.js";
import * as Biometric from "../lib/biometric.js";
import {
  CopyIcon, DownloadIcon, UploadIcon, RefreshIcon, FingerprintIcon,
  BellIcon, SparkleIcon, CheckIcon, EyeIcon, EyeOffIcon,
} from "../components/Icons.jsx";

const TABS = [
  { id: "general", label: "General" },
  { id: "ai", label: "AI" },
  { id: "sync", label: "Sync & devices" },
  { id: "security", label: "Security" },
  { id: "backup", label: "Backup" },
];

const CURRENCIES = ["USD", "EUR", "GBP", "AUD", "CAD", "INR", "JPY", "NZD", "SGD", "CHF"];

export function SettingsDialog({ onClose }) {
  const [tab, setTab] = useState("general");

  return (
    <Modal title="Settings" size="wide" onClose={onClose}>
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "general" && <GeneralTab />}
      {tab === "ai" && <AITab />}
      {tab === "sync" && <SyncTab />}
      {tab === "security" && <SecurityTab />}
      {tab === "backup" && <BackupTab onClose={onClose} />}
    </Modal>
  );
}

/* ------------------------------------------------------------------------- */

function GeneralTab() {
  const { setting, patchSetting, SETTING_IDS, entries, transactions, goals } = useVault();
  const toast = useToast();
  const prefs = setting(SETTING_IDS.preferences, {});
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth({ ok: false }));
  }, []);

  return (
    <>
      <div className="field">
        <label htmlFor="s-currency">Currency</label>
        <select id="s-currency" value={prefs.currency || "USD"} style={{ maxWidth: 200 }}
          onChange={async (e) => {
            await patchSetting(SETTING_IDS.preferences, { currency: e.target.value });
            toast.success("Currency updated");
          }}>
          {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <div className="hint-text">Used for the Financials summary, charts and transaction list.</div>
      </div>

      <hr className="divider" />

      <div>
        <div className="panel-title">What's in this vault</div>
        <div className="stat-row">
          <div className="stat"><div className="label">Entries</div><div className="value">{entries.length}</div></div>
          <div className="stat"><div className="label">Transactions</div><div className="value">{transactions.length}</div></div>
          <div className="stat"><div className="label">Goals</div><div className="value">{goals.length}</div></div>
        </div>
      </div>

      <hr className="divider" />

      <div>
        <div className="panel-title">Backend</div>
        <div className="row" style={{ gap: "var(--s-2)" }}>
          <span className={`sync-dot ${health?.ok ? "ok" : "error"}`} />
          <span className="small">
            {health === null ? "Checking…"
              : health.ok ? `API reachable · PostgreSQL ${health.db} · AI proxy ${health.ai ? "on" : "off"}`
              : "API unreachable — AIDIA is running from its local cache."}
          </span>
        </div>
      </div>
    </>
  );
}

function AITab() {
  const { setting, patchSetting, SETTING_IDS } = useVault();
  const toast = useToast();
  const prefs = setting(SETTING_IDS.preferences, {});

  const [serverAI, setServerAI] = useState(null);
  const [apiKey, setApiKey] = useState(prefs.apiKey || "");
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    api.aiStatus().then(setServerAI).catch(() => setServerAI({ enabled: false }));
  }, []);

  const save = async (patch) => {
    await patchSetting(SETTING_IDS.preferences, patch);
    toast.success("AI settings saved");
  };

  return (
    <>
      <div className="field">
        <label>How AIDIA reaches Claude</label>
        <label className="checkbox">
          <input type="checkbox" checked={!!prefs.useBackend}
            onChange={(e) => save({ useBackend: e.target.checked })} />
          Route through the backend — the key stays on the server, never in this browser
        </label>
        <div className="hint-text">
          {serverAI === null ? "Checking the server…"
            : serverAI.enabled
            ? "The backend has an Anthropic key configured and is ready."
            : "The backend has no ANTHROPIC_API_KEY set — add one to backend/.env and restart it, or use your own key below."}
        </div>
      </div>

      {!prefs.useBackend && (
        <div className="field">
          <label htmlFor="s-key">Your Anthropic API key</label>
          <div className="row">
            <input id="s-key" type={showKey ? "text" : "password"} value={apiKey} className="mono"
              onChange={(e) => setApiKey(e.target.value)} placeholder="sk-ant-…" />
            <button className="icon-btn" onClick={() => setShowKey((s) => !s)}
              aria-label={showKey ? "Hide key" : "Show key"}>
              {showKey ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
            </button>
            <button className="btn btn-ghost" onClick={() => save({ apiKey: apiKey.trim() })}>Save</button>
          </div>
          <div className="hint-text">
            Stored AES-encrypted in your vault, like every other secret here. Get one from
            console.anthropic.com.
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="s-model">Model</label>
        <select id="s-model" value={prefs.model || AI.DEFAULT_MODEL} style={{ maxWidth: 320 }}
          onChange={(e) => save({ model: e.target.value })}>
          {AI.MODELS.map((m) => (
            <option key={m.id} value={m.id}>{m.label} — {m.note}</option>
          ))}
        </select>
      </div>

      <hr className="divider" />

      <p className="hint-text">
        <SparkleIcon size={13} /> AI features are optional — everything else in AIDIA works
        without them. When you use one, the relevant note text is decrypted and sent to
        Anthropic. <strong>Stored password values are always withheld.</strong>
      </p>
    </>
  );
}

function SyncTab() {
  const { meta, sync, syncState } = useVault();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!meta?.syncId || !meta?.token) return;
    try {
      setStatus(await api.getVaultStatus(meta.syncId, meta.token));
    } catch (err) {
      setStatus({ error: err.message });
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [meta?.syncId]);

  const copy = (value, what) => {
    navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
  };

  return (
    <>
      <p className="small secondary">
        Add another device by choosing <strong>Join a synced vault</strong> on its lock
        screen and entering these two values plus your master password. The server stores
        only ciphertext — it cannot read anything you keep here.
      </p>

      <div className="field">
        <label>Sync ID</label>
        <div className="row">
          <input type="text" readOnly value={meta?.syncId || ""} className="mono" />
          <button className="icon-btn" onClick={() => copy(meta?.syncId, "Sync ID")} aria-label="Copy Sync ID">
            <CopyIcon size={16} />
          </button>
        </div>
      </div>

      <div className="field">
        <label>Sync token</label>
        <div className="row">
          <input type={showToken ? "text" : "password"} readOnly value={meta?.token || ""} className="mono" />
          <button className="icon-btn" onClick={() => setShowToken((s) => !s)}
            aria-label={showToken ? "Hide token" : "Show token"}>
            {showToken ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
          <button className="icon-btn" onClick={() => copy(meta?.token, "Sync token")} aria-label="Copy token">
            <CopyIcon size={16} />
          </button>
        </div>
        <div className="hint-text">Treat this like a password — it gates access to the stored ciphertext.</div>
      </div>

      <div className="row" style={{ gap: "var(--s-2)" }}>
        <button className="btn btn-primary" disabled={busy}
          onClick={async () => {
            setBusy(true);
            await sync();
            await refresh();
            setBusy(false);
            toast.success("Sync run");
          }}>
          {busy ? <span className="spinner" /> : <RefreshIcon size={15} />} Sync now
        </button>
        <span className="small muted">
          {syncState.status === "ok" && syncState.at
            ? `Last synced ${new Date(syncState.at).toLocaleTimeString()}`
            : syncState.error || ""}
        </span>
      </div>

      <hr className="divider" />

      {status?.error && <div className="error-text">{status.error}</div>}

      {status && !status.error && (
        <>
          <div>
            <div className="panel-title">Stored on the server</div>
            <div className="stat-row">
              {status.records.map((r) => (
                <div className="stat" key={r.kind}>
                  <div className="label" style={{ textTransform: "capitalize" }}>{r.kind}</div>
                  <div className="value">{r.live}</div>
                  {r.tombstones > 0 && <div className="tiny muted">{r.tombstones} deleted</div>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="panel-title">Devices</div>
            <table className="table">
              <thead><tr><th>Device</th><th>Last seen</th><th className="num">Synced to</th></tr></thead>
              <tbody>
                {status.devices.map((d) => (
                  <tr key={d.id} style={{ cursor: "default" }}>
                    <td>{d.label || d.id.slice(0, 8)}{d.id === api.deviceId() ? " · this device" : ""}</td>
                    <td className="muted small">{new Date(d.last_seen_at).toLocaleString()}</td>
                    <td className="num">#{d.last_rev}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function SecurityTab() {
  const { verifyPassword } = useVault();
  const toast = useToast();
  const [enrolled, setEnrolled] = useState(false);
  const [notifyState, setNotifyState] = useState("default");
  const supported = Biometric.isSupported?.();

  useEffect(() => {
    setEnrolled(!!Biometric.isEnrolled?.());
    setNotifyState(Notify.permission?.() || "default");
  }, []);

  return (
    <>
      <div className="field">
        <label><FingerprintIcon size={14} /> Biometric unlock</label>
        {!supported ? (
          <div className="hint-text">This browser or device doesn't offer WebAuthn biometrics.</div>
        ) : enrolled ? (
          <div className="row" style={{ gap: "var(--s-2)" }}>
            <span className="badge success"><CheckIcon size={11} /> Enrolled</span>
            <button className="btn btn-ghost btn-sm"
              onClick={async () => { await Biometric.disable?.(); setEnrolled(false); toast.success("Biometrics removed"); }}>
              Remove
            </button>
          </div>
        ) : (
          <EnrollBiometric onDone={() => setEnrolled(true)} verifyPassword={verifyPassword} />
        )}
        <div className="hint-text">
          Uses Touch ID / Windows Hello. Where the browser supports the PRF extension this
          unlocks the vault outright; otherwise it re-authenticates password reveals.
        </div>
      </div>

      <hr className="divider" />

      <div className="field">
        <label><BellIcon size={14} /> Device reminders</label>
        <div className="row" style={{ gap: "var(--s-2)" }}>
          <span className={`badge ${notifyState === "granted" ? "success" : "neutral"}`}>
            {notifyState === "granted" ? "Allowed" : notifyState === "denied" ? "Blocked" : "Not asked"}
          </span>
          {notifyState !== "granted" && (
            <button className="btn btn-ghost btn-sm"
              onClick={async () => setNotifyState(await Notify.requestPermission())}>
              Enable notifications
            </button>
          )}
        </div>
        <div className="hint-text">
          {Notify.supportsTriggers?.()
            ? "Reminders are scheduled with the browser's notification triggers, so they fire even when AIDIA is closed."
            : "This browser can't schedule notifications in advance — reminders appear when AIDIA is open."}
        </div>
      </div>

      <hr className="divider" />

      <div className="field">
        <label>Auto-lock</label>
        <div className="hint-text">
          The vault locks itself after 10 minutes of inactivity, and the countdown is paused
          while an editor is open so a long writing session is never interrupted.
        </div>
      </div>
    </>
  );
}

function EnrollBiometric({ onDone, verifyPassword }) {
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="row" style={{ gap: "var(--s-2)" }}>
      <input type="password" value={password} placeholder="Confirm master password"
        onChange={(e) => setPassword(e.target.value)} />
      <button className="btn btn-ghost" disabled={busy || !password}
        onClick={async () => {
          setBusy(true);
          try {
            if (!(await verifyPassword(password))) throw new Error("That password is not correct.");
            await Biometric.enroll(password);
            onDone();
            toast.success("Biometrics enabled");
          } catch (err) {
            toast.error(err.message);
          } finally {
            setBusy(false);
          }
        }}>
        {busy ? <span className="spinner" /> : <FingerprintIcon size={15} />} Enable
      </button>
    </div>
  );
}

function BackupTab({ onClose }) {
  const { exportBackup, importBackup } = useVault();
  const toast = useToast();
  const [pendingImport, setPendingImport] = useState(null);

  const doExport = () => {
    try {
      const blob = new Blob([JSON.stringify(exportBackup(), null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aidia-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <div className="field">
        <label>Export</label>
        <button className="btn btn-ghost" onClick={doExport} style={{ alignSelf: "flex-start" }}>
          <DownloadIcon size={15} /> Download encrypted backup
        </button>
        <div className="hint-text">
          Every record stays AES-encrypted inside the file. Only the key-derivation salt is in
          the clear, and it is not a secret. Restoring needs the master password the backup was
          made with. The sync token is deliberately left out.
        </div>
      </div>

      <hr className="divider" />

      <div className="field">
        <label>Import</label>
        <label className="btn btn-ghost" style={{ alignSelf: "flex-start", cursor: "pointer" }}>
          <UploadIcon size={15} /> Choose a backup file
          <input type="file" accept="application/json,.json" className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                setPendingImport(JSON.parse(await file.text()));
              } catch {
                toast.error("That file isn't valid JSON.");
              }
              e.target.value = "";
            }} />
        </label>
        <div className="hint-text">
          Importing replaces everything on this device and locks the vault, so you unlock again
          with that backup's master password.
        </div>
      </div>

      {pendingImport && (
        <ConfirmDialog title="Replace this vault?" danger confirmLabel="Import and replace"
          message={`This device's current contents will be replaced by the backup taken on ${
            pendingImport.exportedAt ? new Date(pendingImport.exportedAt).toLocaleString() : "an unknown date"
          }. You'll be asked to unlock with that backup's master password.`}
          onCancel={() => setPendingImport(null)}
          onConfirm={async () => {
            try {
              await importBackup(pendingImport);
              toast.success("Backup restored — unlock to continue");
              onClose();
            } catch (err) {
              toast.error(err.message);
            }
          }} />
      )}
    </>
  );
}
